/**
 * Logger estructurado para la aplicación
 * Proporciona logging consistente con niveles, contexto y metadata
 */

import * as Sentry from '@sentry/react';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface LogContext {
  [key: string]: unknown;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

/**
 * Logger estructurado
 */
class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  /**
   * Formatea un log entry para consola
   */
  private formatLogEntry(level: LogLevel, message: string, context?: LogContext, error?: Error): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
    };

    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return entry;
  }

  /**
   * Escribe el log en consola con formato apropiado
   */
  private writeToConsole(entry: LogEntry): void {
    const { level, message, context, error, timestamp } = entry;

    // En desarrollo, mostrar logs detallados con colores
    if (this.isDevelopment) {
      const style = this.getConsoleStyle(level);
      const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
      
      if (error) {
        console.group(`%c${prefix} ${message}`, style);
        console.error('Error:', error);
        if (context) {
          console.log('Context:', context);
        }
        console.groupEnd();
      } else if (context) {
        console.log(`%c${prefix} ${message}`, style, context);
      } else {
        console.log(`%c${prefix} ${message}`, style);
      }
    } else {
      // En producción, JSON estructurado para sistemas de logging
      console.log(JSON.stringify(entry));
    }
  }

  /**
   * Obtiene el estilo de consola según el nivel
   */
  private getConsoleStyle(level: LogLevel): string {
    const styles: Record<LogLevel, string> = {
      [LogLevel.DEBUG]: 'color: #6b7280; font-weight: normal',
      [LogLevel.INFO]: 'color: #3b82f6; font-weight: bold',
      [LogLevel.WARN]: 'color: #f59e0b; font-weight: bold',
      [LogLevel.ERROR]: 'color: #ef4444; font-weight: bold',
    };
    return styles[level] || '';
  }

  /**
   * Envía el log a un servicio externo (futuro: Sentry, LogRocket, etc.)
   */
  private sendToExternalService(entry: LogEntry): void {
    if (this.isProduction && entry.level === LogLevel.ERROR) {
      if (entry.error) {
        Sentry.captureException(new Error(entry.error.message), {
          tags: { loggerSource: 'structured-logger' },
          extra: entry.context,
        });
      } else {
        Sentry.captureMessage(entry.message, {
          level: 'error',
          tags: { loggerSource: 'structured-logger' },
          extra: entry.context,
        });
      }
    }
  }

  /**
   * Log de debug (solo en desarrollo)
   */
  debug(message: string, context?: LogContext): void {
    if (!this.isDevelopment) return;
    
    const entry = this.formatLogEntry(LogLevel.DEBUG, message, context);
    this.writeToConsole(entry);
  }

  /**
   * Log de información general
   * En producción, no se muestra en consola para evitar exponer información sensible
   */
  info(message: string, context?: LogContext): void {
    const entry = this.formatLogEntry(LogLevel.INFO, message, context);
    // Solo mostrar en desarrollo - en producción no exponer información sensible en consola
    if (this.isDevelopment) {
      this.writeToConsole(entry);
    }
    // En producción, los logs de info no se muestran en consola del navegador
    // para mantener la consola limpia y evitar exponer información sensible
  }

  /**
   * Log de advertencias
   */
  warn(message: string, context?: LogContext): void {
    const entry = this.formatLogEntry(LogLevel.WARN, message, context);
    this.writeToConsole(entry);
    this.sendToExternalService(entry);
  }

  /**
   * Log de errores
   */
  error(message: string, error?: Error, context?: LogContext): void {
    const entry = this.formatLogEntry(LogLevel.ERROR, message, context, error);
    this.writeToConsole(entry);
    this.sendToExternalService(entry);
  }

  /**
   * Helper para loggear errores de Supabase
   */
  supabaseError(message: string, error: unknown, context?: LogContext): void {
    const errorObj = error instanceof Error 
      ? error 
      : new Error(String(error));
    
    const supabaseContext = {
      ...context,
      errorCode: (error as any)?.code,
      errorDetails: (error as any)?.details,
      errorHint: (error as any)?.hint,
    };

    this.error(message, errorObj, supabaseContext);
  }

  /**
   * Helper para loggear eventos de usuario (analytics)
   */
  event(eventName: string, properties?: LogContext): void {
    if (this.isDevelopment) {
      this.info(`Event: ${eventName}`, properties);
    }
    // TODO: Enviar a analytics (PostHog, Plausible, etc.)
    // sendToAnalytics(eventName, properties);
  }
}

// Exportar instancia singleton
export const logger = new Logger();

